import { http } from '../api/http';

export type AdminInvestmentStatus = 'Pendiente' | 'Aprobado' | 'Activa' | 'Rechazado' | 'Finalizada';

export interface AdminInvestmentProjectSummary {
  id: number;
  name: string;
  slug: string;
  investmentStartAt: string | null;
  investmentEndAt: string | null;
  termMonths?: number | null;
  status?: string | null;
  projectState?: string | null;
  isActive?: boolean | null;
  closuresCount?: number | null;
  developer: {
    id: number;
    name: string;
    email: string;
  } | null;
  totalInvested: number;
  investmentsCount: number;
  investorsCount: number;
  currency?: string | null;
}

export interface AdminProjectInvestmentItem {
  orderId: number;
  userId: number;
  userName: string;
  userEmail: string;
  amount: number;
  currency: string;
  investedAt: string;
}

export interface AdminProjectInvestmentsDetail {
  project: AdminInvestmentProjectSummary;
  investments: AdminProjectInvestmentItem[];
  closureSummary?: {
    totalCapital: number;
    totalYield: number;
    totalReturned: number;
    ordersClosed: number;
  };
}

export type CloseInvestmentsPayload = {
  salePrice: number;
  reservePrice: number;
  generalExpenses: number;
};

export type CloseInvestmentsResponse = {
  ok: boolean;
  message?: string;
  summary?: {
    totalInvested: number;
    totalCommission: number;
    netProfit: number;
    profundingShare: number;
    clientShare: number;
  };
};

export interface AdminInvestmentListItem {
  id: number;
  projectId: number;
  projectTitle: string;

  userId: number;
  userName: string;
  userEmail: string;

  amount: number;
  currency: 'MXN' | 'USD';
  status: AdminInvestmentStatus;

  provider?: 'paypal' | 'bank_transfer' | string;
  transactionCode?: string | null;
  invoiceId?: string | null;
  orderId?: string | null;

  createdAt: string; // ISO o display string
}

export interface AdminInvestmentDetail extends AdminInvestmentListItem {
  expectedReturn?: number | null;
  notes?: string | null;
  updatedAt?: string | null;

  project?: {
    id: number;
    title?: string;
    location?: string;
    status?: string;
  } | null;

  user?: {
    id: number;
    name?: string;
    email?: string;
    phone?: string | null;
    rfc?: string | null;
  } | null;

  timeline?: Array<{
    at: string;
    status: AdminInvestmentStatus;
    by?: string | null;
    reason?: string | null;
  }>;
}

export interface AdminInvestmentsQuery {
  q?: string; // texto libre (proyecto, inversionista, código, etc.)
  status?: AdminInvestmentStatus | 'Todos';
  projectId?: number;
  userId?: number;
  from?: string; // YYYY-MM-DD
  to?: string;   // YYYY-MM-DD
  page?: number;
  pageSize?: number;
}

export interface AdminInvestmentsListResponse {
  data: AdminInvestmentListItem[];
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export type UpdateInvestmentStatusPayload = {
  status: AdminInvestmentStatus;
  reason?: string; // si el backend lo soporta para rechazo
};

export const AdminInvestmentsService = {
  getProjects(params?: { q?: string }) {
    const qs = params?.q ? `?q=${encodeURIComponent(params.q)}` : '';
    return http<AdminInvestmentProjectSummary[]>(`/admin/investments/projects${qs}`);
  },

  getProjectDetail(projectId: number) {
    return http<AdminProjectInvestmentsDetail>(`/admin/investments/projects/${projectId}`);
  },

  closeProjectInvestments(projectId: number, payload: CloseInvestmentsPayload) {
    return http<CloseInvestmentsResponse>(`/admin/investments/projects/${projectId}/close`, {
      method: 'POST',
      body: payload,
    });
  },

  getAll(query: AdminInvestmentsQuery = {}) {
    const params = new URLSearchParams();
    if (query.q) params.set('q', query.q);
    if (query.status && query.status !== 'Todos') params.set('status', query.status);
    if (query.projectId) params.set('projectId', String(query.projectId));
    if (query.userId) params.set('userId', String(query.userId));
    if (query.from) params.set('from', query.from);
    if (query.to) params.set('to', query.to);
    if (query.page) params.set('page', String(query.page));
    if (query.pageSize) params.set('pageSize', String(query.pageSize));

    const qs = params.toString();
    return http<AdminInvestmentsListResponse>(`/admin/investments${qs ? `?${qs}` : ''}`);
  },

  /**
   * GET /admin/investments/:id
   */
  getById(id: number) {
    return http<AdminInvestmentDetail>(`/admin/investments/${id}`);
  },

  /**
   * PUT /admin/investments/:id/status
   */
  updateStatus(id: number, payload: UpdateInvestmentStatusPayload) {
    return http<AdminInvestmentDetail>(`/admin/investments/${id}/status`, {
      method: 'PUT',
      body: payload
    });
  }
};
